import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { validateCui } from '@/utils/cui';
import { logger } from '@/utils/logger';

export type CuiStatus =
  | { kind: 'idle' }
  | { kind: 'checking'; cui: string }
  | { kind: 'verified'; cui: string; officialName: string; city: string | null }
  | { kind: 'error'; message: string };

type VerifyCuiResponse =
  | { success: true; cui: string; officialName: string; city: string | null; county: string | null }
  | { success: false; error?: string };

const CUI_ERROR_MESSAGES: Record<string, string> = {
  INVALID_FORMAT: 'This CUI is not valid',
  NOT_FOUND: 'No company found for this CUI',
  INACTIVE: 'This company is no longer active',
  ALREADY_REGISTERED: 'An account already exists for this CUI',
  LOOKUP_UNAVAILABLE: "We couldn't check the CUI right now, please try again later",
};

/** User-facing message for a CUI error code from verify-cui / create-organization-account. */
export function cuiErrorMessage(code: string | undefined): string | null {
  return code ? (CUI_ERROR_MESSAGES[code] ?? null) : null;
}

export function useCuiVerification() {
  const [status, setStatus] = useState<CuiStatus>({ kind: 'idle' });
  // Ignore responses for a CUI the user has since changed.
  const requestId = useRef(0);

  const reset = useCallback(() => {
    requestId.current += 1;
    setStatus({ kind: 'idle' });
  }, []);

  const verify = useCallback(async (raw: string): Promise<CuiStatus> => {
    const validation = validateCui(raw);
    if (!validation.ok) {
      requestId.current += 1;
      const next: CuiStatus =
        validation.reason === 'empty'
          ? { kind: 'idle' }
          : { kind: 'error', message: CUI_ERROR_MESSAGES.INVALID_FORMAT };
      setStatus(next);
      return next;
    }

    const id = ++requestId.current;
    setStatus({ kind: 'checking', cui: validation.cui });

    let next: CuiStatus;
    try {
      const { data, error } = await supabase.functions.invoke<VerifyCuiResponse>('verify-cui', {
        body: { cui: validation.cui },
      });
      if (error || !data) throw error ?? new Error('Empty verify-cui response');
      next = data.success
        ? { kind: 'verified', cui: data.cui, officialName: data.officialName, city: data.city }
        : {
            kind: 'error',
            message: cuiErrorMessage(data.error) ?? CUI_ERROR_MESSAGES.LOOKUP_UNAVAILABLE,
          };
    } catch (err) {
      logger.error('verify-cui failed', err);
      next = { kind: 'error', message: CUI_ERROR_MESSAGES.LOOKUP_UNAVAILABLE };
    }

    if (id === requestId.current) setStatus(next);
    return next;
  }, []);

  return { status, verify, reset };
}
